import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import { Trophy } from "lucide-react";
import ExportDropdown from "./ExportDropdown";

// Sprint D Plan A: dual-state types (legacy + _minor) for revenue aggregates.
interface LeagueRecord {
  id: string;
  name: string;
  status: string;
  cost_per_player: number | null;
  cost_per_player_minor: number | null;
  court_cost_per_match: number | null;
  court_cost_per_match_minor: number | null;
  per_round_price: number | null;
  per_round_price_minor: number | null;
  per_round_price_per_player: number | null;
  per_round_price_per_player_minor: number | null;
  admin_fee_per_match: number | null;
  admin_fee_per_match_minor: number | null;
  currency: string;
}

interface LeagueMatchRecord {
  id: string;
  league_id: string;
  status: string;
}

interface TournamentRecord {
  id: string;
  name: string;
  status: string;
  price_per_player: number | null;
  price_per_player_minor: number | null;
  admin_fee_per_player: number | null;
  admin_fee_per_player_minor: number | null;
  court_cost_total: number | null;
  court_cost_total_minor: number | null;
  currency: string;
  max_players: number | null;
}

interface TournamentPlayerRecord {
  tournament_id: string;
}

interface TrainingSessionRecord {
  id: string;
  status: string;
  player_fee: number | null;
  player_fee_minor: number | null;
  coach_cost_per_session: number | null;
  coach_cost_per_session_minor: number | null;
  court_cost_per_session: number | null;
  court_cost_per_session_minor: number | null;
  currency: string;
  group_name: string | null;
  participant_count: number;
}

interface Props {
  leagues: LeagueRecord[];
  leagueMatches: LeagueMatchRecord[];
  tournaments: TournamentRecord[];
  tournamentPlayers: TournamentPlayerRecord[];
  trainingSessions: TrainingSessionRecord[];
}

const COLORS = {
  league: "hsl(var(--primary))",
  tournament: "hsl(var(--chart-2))",
  training: "hsl(var(--chart-3))",
};

export default function ActivitiesTab({
  leagues,
  leagueMatches,
  tournaments,
  tournamentPlayers,
  trainingSessions,
}: Props) {
  const { t } = useTranslation("admin");
  const isEmpty = leagues.length === 0 && tournaments.length === 0 && trainingSessions.length === 0;

  const leagueMatchCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of leagueMatches) {
      counts[m.league_id] = (counts[m.league_id] || 0) + 1;
    }
    return counts;
  }, [leagueMatches]);

  const tournamentPlayerCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of tournamentPlayers) {
      counts[p.tournament_id] = (counts[p.tournament_id] || 0) + 1;
    }
    return counts;
  }, [tournamentPlayers]);

  const revenueByType = useMemo(() => {
    // Sprint D aggregates: prefer _minor cols. All revenue in öre internally.
    let leagueRevenueMinor = 0;
    for (const league of leagues) {
      const matchCount = leagueMatchCounts[league.id] || 0;
      const courtCostMinor = resolveMoney(league.court_cost_per_match_minor, league.court_cost_per_match, league.currency)?.valueMinor ?? 0;
      leagueRevenueMinor += matchCount * courtCostMinor;
    }

    let tournamentRevenueMinor = 0;
    for (const t of tournaments) {
      const playerCount = tournamentPlayerCounts[t.id] || 0;
      const priceMinor = resolveMoney(t.price_per_player_minor, t.price_per_player, t.currency)?.valueMinor ?? 0;
      tournamentRevenueMinor += playerCount * priceMinor;
    }

    let trainingRevenueMinor = 0;
    for (const s of trainingSessions) {
      const feeMinor = resolveMoney(s.player_fee_minor, s.player_fee, s.currency)?.valueMinor ?? 0;
      trainingRevenueMinor += feeMinor * s.participant_count;
    }

    return [
      { name: t("reports.activities.leagues"), revenue: Math.round(leagueRevenueMinor / 100), fill: COLORS.league },
      { name: t("reports.activities.tournaments"), revenue: Math.round(tournamentRevenueMinor / 100), fill: COLORS.tournament },
      { name: t("reports.activities.training"), revenue: Math.round(trainingRevenueMinor / 100), fill: COLORS.training },
    ];
  }, [leagues, tournaments, trainingSessions, leagueMatchCounts, tournamentPlayerCounts]);

  const leagueRows = useMemo(
    () =>
      leagues.map((l) => ({
        name: l.name,
        matches: leagueMatchCounts[l.id] || 0,
        status: l.status,
      })),
    [leagues, leagueMatchCounts],
  );

  const tournamentRows = useMemo(
    () =>
      tournaments.map((t) => {
        const players = tournamentPlayerCounts[t.id] || 0;
        const max = t.max_players || 0;
        const fillRate = max > 0 ? (players / max) * 100 : 0;
        // Sprint D aggregates: prefer price_per_player_minor.
        const priceMinor = resolveMoney(t.price_per_player_minor, t.price_per_player, t.currency)?.valueMinor ?? 0;
        const revenue = Math.round((players * priceMinor) / 100);
        return {
          name: t.name,
          players,
          max,
          fillRate,
          revenue,
          status: t.status,
        };
      }),
    [tournaments, tournamentPlayerCounts],
  );

  const popularActivities = useMemo(() => {
    const items: { name: string; type: string; participants: number }[] = [];

    for (const l of leagues) {
      items.push({
        name: l.name,
        type: t("reports.activities.league"),
        participants: leagueMatchCounts[l.id] || 0,
      });
    }
    for (const t of tournaments) {
      items.push({
        name: t.name,
        type: t("reports.activities.tournament"),
        participants: tournamentPlayerCounts[t.id] || 0,
      });
    }

    const groupedTraining: Record<string, number> = {};
    for (const s of trainingSessions) {
      const key = s.group_name || s.id;
      groupedTraining[key] = (groupedTraining[key] || 0) + s.participant_count;
    }
    for (const [name, participants] of Object.entries(groupedTraining)) {
      items.push({ name, type: t("reports.activities.training"), participants });
    }

    return items.sort((a, b) => b.participants - a.participants);
  }, [leagues, tournaments, trainingSessions, leagueMatchCounts, tournamentPlayerCounts]);

  const exportData = useMemo(
    () => () =>
      popularActivities.map((a) => ({
        [t("reports.activities.name")]: a.name,
        [t("reports.activities.type")]: a.type,
        [t("reports.activities.participants")]: a.participants,
      })),
    [popularActivities],
  );

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        {t("reports.activities.noData")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground">{t("reports.activities.leagues")}</p>
            <p className="text-2xl font-bold">{leagues.length}</p>
          </CardContent>
        </Card>
        <Card className="p-4">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground">{t("reports.activities.tournaments")}</p>
            <p className="text-2xl font-bold">{tournaments.length}</p>
          </CardContent>
        </Card>
        <Card className="p-4">
          <CardContent className="p-0">
            <p className="text-sm text-muted-foreground">{t("reports.activities.trainingSessions")}</p>
            <p className="text-2xl font-bold">{trainingSessions.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue by activity type */}
      <Card className="p-4">
        <CardHeader className="p-0 pb-4">
          <CardTitle className="text-base">{t("reports.activities.revenueByType")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueByType} layout="vertical" margin={{ left: 80, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v: number) => formatPrice(v, { showCurrency: false })} />
                <YAxis type="category" dataKey="name" width={70} />
                <Tooltip formatter={(value: number) => formatPrice(value)} />
                <Bar dataKey="revenue" name={t("reports.activities.revenue")} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* League detail table */}
      <Card className="p-4">
        <CardHeader className="p-0 pb-4">
          <CardTitle className="text-base">{t("reports.activities.leagueDetails")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("reports.activities.name")}</TableHead>
                <TableHead className="text-right">{t("reports.activities.matches")}</TableHead>
                <TableHead>{t("reports.activities.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leagueRows.map((row) => (
                <TableRow key={row.name}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-right">{row.matches}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{row.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Tournament detail table */}
      <Card className="p-4">
        <CardHeader className="p-0 pb-4">
          <CardTitle className="text-base">{t("reports.activities.tournamentDetails")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("reports.activities.name")}</TableHead>
                <TableHead className="text-right">{t("reports.activities.participants")}</TableHead>
                <TableHead className="text-right">{t("reports.activities.max")}</TableHead>
                <TableHead className="text-right">{t("reports.activities.fillRate")}</TableHead>
                <TableHead className="text-right">{t("reports.activities.revenue")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tournamentRows.map((row) => (
                <TableRow key={row.name}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-right">{row.players}</TableCell>
                  <TableCell className="text-right">{row.max}</TableCell>
                  <TableCell className="text-right">{row.fillRate.toFixed(0)}%</TableCell>
                  <TableCell className="text-right">{formatPrice(row.revenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Popular activities ranked list */}
      <Card className="p-4">
        <CardHeader className="p-0 pb-4 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            {t("reports.activities.mostPopular")}
          </CardTitle>
          <ExportDropdown getData={exportData} filePrefix="aktiviteter" />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead>{t("reports.activities.name")}</TableHead>
                <TableHead>{t("reports.activities.type")}</TableHead>
                <TableHead className="text-right">{t("reports.activities.participants")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {popularActivities.map((a, i) => (
                <TableRow key={`${a.type}-${a.name}`}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{a.type}</Badge>
                  </TableCell>
                  <TableCell className="text-right">{a.participants}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
